"use client";

import * as React from "react";

import type { ICustomLayerBoundaryDetails } from "@/providers/store/api/custom-layers/custom-layers.interface";
import type {
  IProjectScheduledOccupancyItem,
  ProjectReviewStatusEnum,
  ProjectStatusEnum,
} from "@/providers/store/api/projects/projects.interface";
import type { IStudentListPreviewRequest } from "@/providers/store/api/reports/reports.interface";
import type {
  ISchoolBoundaryResponse,
  ISchoolLastActionResponse,
  ISchoolWithDetailsResponse,
} from "@/providers/store/api/schools/schools.interface";
import { type StudentCountsInShapeColorByEnum } from "@/providers/store/api/shapes/enums/student-counts-in-shape.enum";
import type { IBoundarySchoolLink } from "@/shared/types/boundary-school-link.types";

import type {
  MapPanelSide,
  MapPanelTab,
  MapPanelTabKey,
} from "./map-panel.types";

export enum OtherInfoItemTypeEnum {
  badge = "badge",
  keyValue = "keyValue",
}

export interface OtherInfoItem {
  type: "badge" | "keyValue";
  label: string;
  value?: string;
}

export enum SelectedDataType {
  STUDENT = "student",
  SCHOOL = "school",
  SELECTION = "selection",
  BOUNDARY = "boundary",
  STUDENT_CLUSTER = "studentCluster",
  SCHOOL_CLUSTER = "schoolCluster",
  PROJECT = "project",
  PROJECT_CLUSTER = "projectCluster",
  ZONES = "zones",
  SEARCH_MARKER = "searchMarker",
}

export interface StudentCountsDataByRow {
  name: string;
  color: string;
  plottedNumber: number;
  plottedPercentage: number;
}

export interface IStudentPayload {
  id: string;
  fullName: string;
  grade: string;
  address: string;
  attendingSchool: string;
  districtOfResidence: string;
  newToDistrict: string;
  residenceES: string;
  residenceMS: string;
  residenceHS: string;
  engLangLearnerValue: string | null;
  dualImmersionValue: string | null;
  raceAndEthnicity: string[];
  otherInfo: OtherInfoItem[];
}

export interface FileItem {
  fileName: string;
  fileSizeBytes: number;
  fileId?: string;
  mimeType?: string;
  driveNodeId?: string;
  onView?: () => void;
  onDownload?: () => void;
}

export interface ISchoolPayload {
  closedAt?: string;
  name: string;
  schoolId: string;
  schoolDriveId?: string;
  schoolLevelIds?: string[];
  schoolTypeLabels?: string[];
  gradeServed: string;
  principalName: string;
  phoneNumber: string;
  schoolURL: string;
  schoolURLHref: string;
  address: string;
  year: string;
  capacity: string;
  educationalProgramType?: string | null;
  files: FileItem[];
  lastAction: ISchoolLastActionResponse | null;
  futureAction: ISchoolLastActionResponse | null;
  schoolBoundaries: ISchoolBoundaryResponse[];
  color?: string;
}

export interface ISelectionPayload {
  studentsCount: number;
  dataCount: number;
  schoolsCount?: number;
  schoolsPlotted?: number;
  colorBy: StudentCountsInShapeColorByEnum;
  useSchoolCountsOnly?: boolean;
  shapes: {
    id: string;
    label: string;
    value: string;
    onRemove?: () => void;
    previewCoordinates?: number[][];
    previewColor?: string;
    schoolId?: string;
    schoolDriveId?: string;
    customLayerId?: string;
    boundaryId?: string;
    mapLevelId?: string;
    note?: string | null;
  }[];
  dataBy: StudentCountsDataByRow[];
  getSchoolDetails?: (
    schoolId: string,
  ) => Promise<ISchoolWithDetailsResponse | null>;
  getCustomBoundaryDetails?: (
    layerId: string,
    boundaryId: string,
  ) => Promise<ICustomLayerBoundaryDetails | null>;
  getStudentListPreviewRequest?: () => IStudentListPreviewRequest | null;
  isDistrictWide?: boolean;
}

export interface IStudentClusterPayload {
  studentsCount: number;
  colorBy: StudentCountsInShapeColorByEnum;
  dataBy: StudentCountsDataByRow[];
  getStudentListPreviewRequest: () => IStudentListPreviewRequest | null;
}

export interface ISchoolClusterListItem {
  schoolId: string;
  schoolName: string;
  schoolTypes: string[];
}

export interface ISchoolClusterPayload {
  schoolsCount: number;
  schools: ISchoolClusterListItem[];
}

export interface IProjectClusterListItem {
  projectId: string;
  projectName: string;
}

export interface IProjectClusterPayload {
  projectsCount: number;
  unitsCount: number;
  projects: IProjectClusterListItem[];
}

export interface IProjectPayload {
  id: string;
  name: string;
  projectType: string;
  reviewStatus: ProjectReviewStatusEnum | string;
  address: string;
  scheduledOccupancy: IProjectScheduledOccupancyItem[];
  uniqueStatuses: (ProjectStatusEnum | string)[];
}

export interface IBoundaryPlottingPayload {
  studentsCount: number;
  dataCount: number;
  schoolsCount: number;
  schoolsPlotted: number;
  colorBy: StudentCountsInShapeColorByEnum;
  dataBy: StudentCountsDataByRow[];
  getStudentListPreviewRequest: () => IStudentListPreviewRequest | null;
  useSchoolCountsOnly: boolean;
}

export interface IBoundaryPayload {
  schoolName: string | null;
  schoolInternalId: string;
  schoolBoundaryId?: string | null;
  previewCoordinates?: number[][];
  gradesRange: string | null;
  address: string | null;
  color?: string | null;
  schoolId?: string | null;
  boundaryId?: string;
  schoolTypes?: readonly string[];
  /** Full school links for edit mode; primary fields above stay for header / preview. */
  schools?: IBoundarySchoolLink[];
  plotting?: IBoundaryPlottingPayload;
  isPlottingLoading?: boolean;
  /** True for non-school boundaries (district): hides the Connected school section. */
  isSchoolConnectionDisabled?: boolean;
}

export interface IZonesListItem {
  previewId: string;
  documentId: string;
  label: string;
  previewCoordinates?: number[][];
  previewColor?: string;
  schoolId?: string | null;
}

export interface IZonesPayload {
  zonesCount?: number;
  unassignedCount?: number;
  sectionTitle: string;
  zones: IZonesListItem[];
  showSummary?: boolean;
}

export interface ISearchMarkerPayload {
  address: string;
}

export type SelectedData =
  | { type: SelectedDataType.STUDENT; payload: IStudentPayload | null }
  | { type: SelectedDataType.SCHOOL; payload: ISchoolPayload | null }
  | { type: SelectedDataType.SELECTION; payload: ISelectionPayload | null }
  | { type: SelectedDataType.BOUNDARY; payload: IBoundaryPayload | null }
  | {
      type: SelectedDataType.STUDENT_CLUSTER;
      payload: IStudentClusterPayload | null;
    }
  | {
      type: SelectedDataType.SCHOOL_CLUSTER;
      payload: ISchoolClusterPayload | null;
    }
  | { type: SelectedDataType.PROJECT; payload: IProjectPayload | null }
  | {
      type: SelectedDataType.PROJECT_CLUSTER;
      payload: IProjectClusterPayload | null;
    }
  | { type: SelectedDataType.ZONES; payload: IZonesPayload | null }
  | {
      type: SelectedDataType.SEARCH_MARKER;
      payload: ISearchMarkerPayload | null;
    }
  | null;

interface IMapPanelContextValue {
  tabs: MapPanelTab[];
  side: MapPanelSide;
  activeKey: MapPanelTabKey;
  setActiveKey: (key: MapPanelTabKey) => void;
  open: boolean;
  setOpen: (value: boolean) => void;
  selectedData: SelectedData;
  setSelectedData: (data: SelectedData) => void;
}

const MapPanelContext = React.createContext<IMapPanelContextValue | null>(null);

export function useMapPanelContext() {
  const ctx = React.useContext(MapPanelContext);
  if (!ctx) {
    throw new Error("MapPanel.* must be used inside <MapPanel />");
  }
  return ctx;
}

export { MapPanelContext };
